import React, { useMemo } from "react";
import { Button, Popconfirm, Tooltip, message } from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  WarningOutlined,
  PlusOutlined,
  ClusterOutlined,
  StopOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import "./layouts-styles.css";
import { checkPermissions } from "@comps/commonFunctions";

const classes = "da-px-10 da-my-0";

const notAllowed = "You are not allowed to perform this action.";

const unavailable = "This action is not available for this record.";

const noPermission = (verb) => `You do not have permission to ${verb} this record.`;


/**
 * Every row action icon is rendered at exactly this many px, whatever the screen
 * passed in - antd icons size off font-size, MUI icons default to a 1.5rem svg,
 * and a few screens hand over a raw <svg>/<img>. Keep in sync with
 * `--base-action-icon-size` in layouts-styles.css.
 */
const ICON_SIZE = 15;

const ICON_BOX_STYLE = {
  width: ICON_SIZE,
  height: ICON_SIZE,
  minWidth: ICON_SIZE,
  minHeight: ICON_SIZE,
  fontSize: ICON_SIZE,
  lineHeight: 1,
};

/**
 * Force any icon node into the same square box so icons line up on one baseline
 * across every table. The element itself is cloned with explicit px sizing (which
 * beats both antd's font-size sizing and MUI's `.MuiSvgIcon-root` em sizing), then
 * wrapped in a fixed-size flex box so even a non-element icon (string, number,
 * fragment) still occupies an identical slot.
 */
const sizeGlyph = (icon, boxed) =>
  React.cloneElement(icon, {
    className: [icon.props?.className, "base-action-icon__glyph"].filter(Boolean).join(" "),
    style: {
      ...(icon.props?.style || {}),
      // font-size drives both families: antd renders an em-sized svg, MUI's
      // .MuiSvgIcon-root is 1em wide/tall once its 1.5rem default is overridden.
      fontSize: ICON_SIZE,
      lineHeight: 1,
      ...(boxed ? { width: ICON_SIZE, height: ICON_SIZE } : {}),
    },
  });

/**
 * `boxed` (the default) also wraps the icon in a fixed square so every button's
 * glyph sits on the same baseline. Standalone icons that a screen renders as their
 * own clickable slot are only resized - wrapping them would shrink their hit area.
 */
const normalizeIcon = (icon, boxed = true) => {
  if (icon === null || icon === undefined || icon === false) return null;

  const glyph = React.isValidElement(icon) ? sizeGlyph(icon, boxed) : icon;

  if (!boxed) return glyph;

  return (
    <span className="base-action-icon" style={ ICON_BOX_STYLE }>
      { glyph }
    </span>
  );
};

/**
 * Screens also drop raw nodes into BaseAction as `children` - usually
 * `<Button icon={<SomeIcon />} />`, sometimes wrapped in a Tooltip/Popconfirm, and
 * occasionally a bare icon element with an onClick. Those never pass through
 * ActionButton, so they used to keep whatever size their icon library defaults to
 * (MUI's 24px lock next to antd's 15px eye). This detects icon elements anywhere in
 * that subtree.
 */
const ICON_NAME = /(Outlined|Filled|TwoTone|Rounded|Sharp|Icon)$/;

const isIconElement = (node) => {
  const type = node?.type;
  if (!type) return false;
  if (type === "svg" || type === "img") return true;

  // MUI exports its icons as memo(forwardRef(Component)) and tags `muiName` on the
  // innermost component, so unwrap both layers before looking for it. `displayName`
  // is stripped from antd/MUI icons in production builds - the CSS fallback in
  // layouts-styles.css covers those, this only has to catch what it can.
  const candidates = [type, type.type, type.render, type.type?.render];
  return candidates.some(
    (candidate) => candidate
      && (candidate.muiName === "SvgIcon" || ICON_NAME.test(candidate.displayName || candidate.name || ""))
  );
};

/**
 * Walk a child node and give every icon it contains the same square box. An
 * icon-only button in the subtree also picks up the shared action-button class so
 * it matches the built-in actions instead of rendering as a default antd button.
 */
const normalizeActionNode = (node, depth = 0) => {
  if (!React.isValidElement(node) || depth > 8) return node;

  // a bare icon child is its own clickable slot - resize the glyph, keep the slot
  if (isIconElement(node)) return normalizeIcon(node, false);

  const next = {};

  const iconOnly = node.props?.icon !== undefined
    && (node.props.children === undefined || node.props.children === null);

  if (node.props?.icon !== undefined) {
    next.icon = normalizeIcon(node.props.icon);
    // an icon-only button should share the row-action shell (size, radius, hover)
    if (iconOnly) {
      next.className = [node.props.className, "base-table-action-btn"].filter(Boolean).join(" ");
    }
  }

  // An icon on its own says nothing, so an icon-only child that carries a label
  // gets the same antd tooltip the built-in actions use. The native `title`
  // attribute is dropped so the browser's own tooltip does not double up.
  const childLabel = iconOnly
    ? node.props.tooltip || node.props.title || node.props["aria-label"]
    : null;

  if (childLabel) {
    next.title = undefined;
    return (
      <Tooltip title={ childLabel }>
        <span className="base-table-action-slot">{ React.cloneElement(node, next) }</span>
      </Tooltip>
    );
  }

  if (node.props?.children !== undefined) {
    // Tooltip/Popconfirm accept exactly one child, so a single child must stay a
    // single element - React.Children.map would turn it into a one-item array.
    next.children = Array.isArray(node.props.children)
      ? React.Children.map(node.props.children, (child) => normalizeActionNode(child, depth + 1))
      : normalizeActionNode(node.props.children, depth + 1);
  }

  return Object.keys(next).length ? React.cloneElement(node, next) : node;
};

const getActionButtonClass = (tone, disabled, wide) =>
  [
    classes,
    "base-table-action-btn",
    tone ? `base-table-action-btn--${tone}` : "",
    disabled ? "base-table-action-btn--disabled" : "",
    wide ? "base-table-action-btn--wide" : "",
  ]
    .filter(Boolean)
    .join(" ");

/**
 * Row actions stay in the same position on every row: an action the user cannot
 * perform is rendered disabled with the reason in its tooltip instead of being
 * dropped from the row (which used to re-centre the whole cell).
 *
 * `disabledReason` is also re-checked inside the click handler, so stripping the
 * `disabled` attribute from the DOM still cannot fire the action - it only earns
 * a warning toast. The API remains the real authority; this is UX, not security.
 */
export const ActionButton = ({
  tone,
  title,
  icon,
  text,
  disabledReason,
  onClick,
  className,
  ...rest
}) => {
  const disabled = Boolean(disabledReason);

  const handleClick = (event) => {
    if (disabled) {
      event?.preventDefault?.();
      event?.stopPropagation?.();
      message.warning(typeof disabledReason === "string" ? disabledReason : notAllowed);
      return;
    }
    onClick?.(event);
  };

  const button = (
    <Button
      { ...rest }
      className={ [getActionButtonClass(tone, disabled, Boolean(text)), className].filter(Boolean).join(" ") }
      type="link"
      size="middle"
      disabled={ disabled }
      aria-disabled={ disabled }
      onClick={ handleClick }
    >
      { normalizeIcon(icon) }
      { text ? <span className="klok-btn-label">{ text }</span> : null }
    </Button>
  );

  return (
    <Tooltip title={ disabled ? disabledReason : title }>
      {/* disabled antd buttons swallow pointer events, so the tooltip needs a live wrapper */ }
      <span className="base-table-action-slot">{ button }</span>
    </Tooltip>
  );
};

/**
 * Resolve why an action is unavailable, in priority order:
 *   1. explicit per-row reason from the screen (`disabledActions`)
 *   2. missing module permission
 *   3. the screen passed a falsy handler for this row
 * Returns a string reason, or "" when the action is allowed.
 */
const resolveReason = (override, allowed, verb, handler) => {
  if (override) return typeof override === "string" ? override : notAllowed;
  if (!allowed) return noPermission(verb);
  if (handler !== undefined && typeof handler !== "function") return unavailable;
  return "";
};

/**
 * Screens disable a row action by passing a falsy handler - `cond && onDelete`
 * yields `false`, `cond ? onEdit : ''` yields `""`. Both mean "this screen has
 * the action but not for this row", so the slot is rendered disabled. Only an
 * absent prop (`undefined`) means the screen has no such action at all.
 */
const isDeclared = (handler) => handler !== undefined;

/**
 * Screens describe their own row actions declaratively via `extraActions`, so the
 * disabled-instead-of-hidden rule, the permission check, the confirm dialog and
 * the click guard are implemented once here rather than per table.
 *
 * Each entry: {
 *   key, tone, title, text, icon, onClick,
 *   permission,      // module action to check, e.g. "update"
 *   label,           // verb used in the no-permission message, e.g. "run"
 *   disabled,        // true | "reason shown in the tooltip and the toast"
 *   confirm,         // Popconfirm title; skipped automatically when disabled
 *   hidden,          // drop the slot entirely (use only for screen-level opt-outs)
 * }
 */
/** "runPayroll" / "run-payroll" -> "Run payroll", so a slot is never left unlabelled. */
const humanize = (key) =>
  String(key || "")
    .replace(/[-_]+/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .trim()
    .replace(/^./, (first) => first.toUpperCase());

const renderExtraAction = (action, modkey, index) => {
  if (!action || action.hidden) return null;

  const allowed = action.permission ? checkPermissions(modkey, action.permission) : true;
  const reason = resolveReason(action.disabled, allowed, action.label || "perform this action on");

  const button = (
    <ActionButton
      key={ action.key || `extra-${index}` }
      tone={ action.tone }
      // every action must explain itself on hover, even if the screen forgot a title
      title={ action.title || action.text || humanize(action.key) }
      text={ action.text }
      icon={ action.icon }
      className={ action.className }
      disabledReason={ reason }
      onClick={ action.confirm ? undefined : () => action.onClick?.() }
    />
  );

  // A disabled action never opens its confirm dialog - the button itself warns.
  if (!action.confirm || reason) return button;

  return (
    <Popconfirm
      key={ action.key || `extra-${index}` }
      title={ action.confirm }
      icon={ <WarningOutlined /> }
      onConfirm={ () => {
        if (action.permission && !checkPermissions(modkey, action.permission)) {
          message.warning(noPermission(action.label || "perform this action on"));
          return;
        }
        action.onClick?.();
      } }
    >
      { button }
    </Popconfirm>
  );
};

const BaseAction = (props) => {
  const { modkey, disabledActions = {}, extraActions = [] } = { ...props };

  // cache permission checks (run once per render)
  const canCancel = useMemo(() => checkPermissions(modkey, "cancel"), [modkey]);
  const canCreate = useMemo(() => checkPermissions(modkey, "create"), [modkey]);
  const canView = useMemo(() => checkPermissions(modkey, "view"), [modkey]);
  const canUpdate = useMemo(() => checkPermissions(modkey, "update"), [modkey]);
  const canDelete = useMemo(() => checkPermissions(modkey, "delete"), [modkey]);
  const isApprovalAction = useMemo(
    () => typeof modkey === "string" && (modkey.toLowerCase().includes("approval") || modkey.toLowerCase().includes("delegates")),
    [modkey]
  );

  // One descriptor per built-in action. Rendering, permission handling, the
  // confirm dialog and the click guard are identical for all of them, so every
  // table gets the same behaviour without touching a single screen.
  const CORE = [
    {
      name: "view", prop: "onView", tone: "view", title: "View details", verb: "view",
      allowed: canView, icon: <EyeOutlined />,
    },
    {
      name: "edit", prop: "onEdit", verb: isApprovalAction ? "action" : "edit",
      tone: isApprovalAction ? "approve" : "edit",
      title: isApprovalAction ? "Approve or reject this request" : "Edit record",
      allowed: canUpdate,
      icon: isApprovalAction
        ? <CheckCircleOutlined />
        : <EditOutlined />,
    },
    {
      name: "delete", prop: "onDelete", tone: "delete", title: "Delete record", verb: "delete",
      allowed: canDelete, confirm: "Are you sure?",
      icon: <DeleteOutlined />,
    },
    {
      name: "cancelRequest", prop: "onCancelRequest", tone: "cancel",
      title: "Cancel this request", verb: "cancel", allowed: canCancel,
      icon: <StopOutlined />,
    },
    {
      name: "viewFlow", prop: "onViewFlow", tone: "flow", title: "View approval flow",
      verb: "view the flow of", allowed: canView,
      icon: <ClusterOutlined />,
    },
    {
      name: "viewHistory", prop: "onViewHistory", tone: "history", title: "View history",
      verb: "view the history of", allowed: canView,
      icon: <ClockCircleOutlined />,
    },
    {
      name: "add", prop: "onAdd", tone: "add", title: "Add new record",
      verb: "add against", allowed: canCreate,
      icon: <PlusOutlined />,
    },
  ];

  const renderCore = (action) => {
    const handler = props[action.prop];
    if (!isDeclared(handler)) return null;

    const reason = resolveReason(disabledActions[action.name], action.allowed, action.verb, handler);
    const run = () => runAction(handler, reason, props.each);

    const button = (
      <ActionButton
        key={ action.name }
        tone={ action.tone }
        title={ action.title }
        icon={ action.icon }
        disabledReason={ reason }
        onClick={ action.confirm ? undefined : run }
      />
    );

    // A disabled action never opens its confirm dialog - the button itself warns.
    if (!action.confirm || reason) return button;

    return (
      <Popconfirm
        key={ action.name }
        title={ action.confirm }
        icon={ <WarningOutlined /> }
        onConfirm={ run }
      >
        { button }
      </Popconfirm>
    );
  };

  const actions = [
    ...CORE.map(renderCore),
    props.children
      ? (
        <React.Fragment key="children">
          { React.Children.map(props.children, (child) => normalizeActionNode(child)) }
        </React.Fragment>
      )
      : null,
    ...extraActions.map((action, index) => renderExtraAction(action, modkey, index)),
  ].filter(Boolean);

  return (
    <div className="base-table-actions-inline">
      { actions }
    </div>
  );
};

/**
 * Last line of defence: even if the `disabled` attribute is stripped from the
 * DOM, the handler still refuses to run and explains why. The API remains the
 * real authority - this is UX, not security.
 */
const runAction = (handler, reason, each) => {
  if (reason) {
    message.warning(reason);
    return;
  }
  if (typeof handler !== "function") {
    message.warning(unavailable);
    return;
  }
  handler(each);
};

export default BaseAction;
